import React, { useCallback, useEffect, useRef, useState } from "react"

import { createContext } from "@/shared/utils"
import { ComponentProps } from "@/stitches/types"

import { SAccordion } from "./Accordion.styles"

type AccordionContextValue = {
  selected: string[]
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  disabled: boolean
  onItemOpen: (value: string) => void
  onItemClose: (value: string) => void
}

export const [AccordionProvider, useAccordionContext] = createContext<AccordionContextValue>("Accordion")

interface IAccordionProps extends ComponentProps<typeof SAccordion> {
  /**
   * Array of Accordion Item value(s) to be open by default
   */
  defaultSelected?: string[]
  disabled?: boolean
  /**
   * Enable multiple panels open
   */
  multiple?: boolean
  /**
   * Callback with an array of string values of the Accordion Items
   */
  onSelectedChange?: (selected: string[]) => void
}

export const Accordion = ({
  defaultSelected = [],
  disabled = false,
  multiple,
  onSelectedChange,
  ...props
}: IAccordionProps) => {
  const [selected, setSelected] = useState(defaultSelected)
  const selectedRef = useRef(selected)

  const handleItemOpen = useCallback(
    (itemValue: string) =>
      !multiple ? setSelected([itemValue]) : setSelected((prevValue = []) => [...prevValue, itemValue]),
    [multiple, setSelected],
  )

  const handleItemClose = useCallback(
    (itemValue: string) =>
      !multiple
        ? setSelected([])
        : setSelected((prevValue = []) => prevValue.filter((value) => value !== itemValue)),
    [multiple, setSelected],
  )

  useEffect(() => {
    if (selected !== selectedRef.current) {
      selectedRef.current = selected
      onSelectedChange && onSelectedChange(selected)
    }
  }, [onSelectedChange, selected])

  return (
    <AccordionProvider
      selected={selected}
      setSelected={setSelected}
      disabled={disabled}
      onItemOpen={handleItemOpen}
      onItemClose={handleItemClose}
    >
      <SAccordion data-ui="accordion" data-testid="accordion" {...props} />
    </AccordionProvider>
  )
}
